import styled, {css} from "styled-components";
import {baseDistance, ELLIPSIS, FlexCenterStyle, fontSize, radius, VerticalFlexStyle, zIndex} from "../../../styles/styles";
import {colorPalette} from "../../../styles/colorPalette";
import {PillStyled} from "../../../input/select/SelectInputV5";
import {SelectedOptionStyled} from "../../../input/search/SelectSearchFieldV5";
import {SearchFieldStyled} from "../../../input/search/SearchField.styled";

export const MultiSelectInputStyled = styled.div<{$emptyInline?: boolean}>`
	border: 1px solid ${colorPalette.gray.c300};
	// No horizontal padding: the pills' own padding puts the text where the other fields (and single-select pills) have it
	padding: ${baseDistance.xs} 0;

	${(props) => {
		if (props.$emptyInline) {
			return css`
				max-height: 29px;
			`;
		}
	}}

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.empty {
		height: ${baseDistance.lg};
		cursor: pointer;
	}

	&.isInherited {
		color: ${colorPalette.gray.c700Dark};
	}

	// Nothing happens on click, so don't suggest otherwise. Parent contexts (details panel, table cells) define their own
	// hover effects with higher specificity, hence the !important
	&.disabled {
		cursor: default !important;

		${PillStyled} {
			cursor: default;
		}

		&:hover {
			background-color: transparent !important;
		}

		&.inline:hover {
			border-color: transparent !important;
		}
	}
`;

export const MultiSelectOptionStyled = styled(SelectedOptionStyled)`
	${VerticalFlexStyle};
	gap: ${baseDistance.xs};
	flex: 1;
	min-width: 0;

	${PillStyled} {
		${FlexCenterStyle};
		justify-content: space-between;
		padding-right: ${baseDistance.xs};

		span {
			min-width: 0;
			${ELLIPSIS};
			padding-right: 0;
		}
	}
`;

export const DropdownStyled = styled.div<{$noFixedPosition: boolean}>`
	z-index: ${zIndex.contextOptions};
	background-color: ${colorPalette.white};
	max-height: 400px;
	position: ${(props) => (props.$noFixedPosition ? "absolute" : "fixed")};
	box-shadow: 0px 4px 8px 0px #00000033;
	padding: ${baseDistance.sm};
	border-radius: ${radius.md};
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};
	box-sizing: border-box;
	min-width: 320px;
	max-width: 600px;
	overflow: hidden;

	${SearchFieldStyled} {
		width: 100%;

		input {
			height: 32px;
		}
	}
`;

export const ListStyled = styled.div`
	overflow: auto;
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.xs};
	min-width: 0;
	width: 100%;
	max-width: 100%;
`;

export const ListItemOuterStyled = styled.div`
	min-height: 32px;
	flex-shrink: 0;
	width: 100%;
	min-width: 0;
	border-radius: ${radius.sm};
	padding: ${baseDistance.xs} ${baseDistance.xs} ${baseDistance.xs} 0;
	background-color: ${colorPalette.white};
	display: flex;
	align-items: center;
	justify-content: flex-start;

	.checkbox {
		flex-shrink: 0;
	}

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.selected {
		border: none;
		background-color: ${colorPalette.primary.c200Light};
	}
`;

export const ListItemStyled = styled.div<{$backgroundColor: string; $fontColor: string}>`
	display: flex;
	flex: 1;
	min-width: 0;
	gap: ${baseDistance.sm};
	height: 24px;
	font-size: ${fontSize.sm};
	align-items: center;
	cursor: pointer;
	border-radius: ${radius.sm};
	padding-left: ${baseDistance.xs};
	background-color: ${(props) => props.$backgroundColor ?? `${colorPalette.gray.c100}`};
	color: ${(props) => props.$fontColor};

	.label {
		${ELLIPSIS};
		min-width: 0;
		flex: 1;
		display: block;
		height: 100%;
		line-height: 24px;
	}
`;

export const BelowItemsStyled = styled.div`
	display: flex;
	justify-content: center;
	margin: ${baseDistance.xs} 0 0 0;
	height: 24px;

	&:hover {
		background-color: ${colorPalette.gray.c300};
		border-radius: ${radius.xs};
		cursor: pointer;
	}
`;

export const ClearAllButtonStyled = styled.div`
	${FlexCenterStyle};
	align-self: flex-end;
	justify-content: flex-end;
	width: 56px;
	height: 24px;
	padding: ${baseDistance.xs};
	font-size: ${fontSize.sm};
	border-radius: ${radius.sm};
	cursor: pointer;

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.disabled {
		opacity: 0.5;
		cursor: default;
		pointer-events: none;
	}
`;
